import { put, select, takeEvery } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { logger } from "@tecma/logs-fe";

import SpaceService from "modules/spaces/infrastructure/http/newspace.client";
import routesPaths from "routes/routesPaths";
import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { roomsSelectors } from "store/slices/rooms/selectors";
import {
  setCurrentRoom,
  setEndRoomLoading,
  setEndRoomsLoading,
  setRoomLoading,
  setRooms,
  setRoomsLoading,
} from "store/slices/rooms/slice";
import { EnvironmentState } from "store/slices/environment/slice";
import { environmentSelectors } from "store/slices/environment/selector";
import { StartEndDates } from "types/dates";
import { RoomEntity, RoomFilters } from "types/rooms";

import { roomsActions } from "./rooms.actions";
import { getAvailableRoomsByUserSelection } from "./rooms.api";

function* getRoomsByProjectSaga(
  action: PayloadAction<{
    dates: StartEndDates;
    filters?: RoomFilters;
    pagination?: {
      page: number;
      perPage: number;
    };
  }>,
) {
  yield put(roomsActions.getRoomsByProjectInProgress());
  const projectId: string = yield select(applicationStateSelectors.projectID);
  const currentPaginatedRooms: RoomEntity[] = yield select(
    roomsSelectors.selectRooms,
  );

  const newRooms: Awaited<ReturnType<typeof getAvailableRoomsByUserSelection>> =
    yield getAvailableRoomsByUserSelection({
      projectId,
      range: {
        from: action?.payload?.dates.startDate,
        to: action?.payload?.dates.endDate,
      },
      filters: action?.payload?.filters,
      // pagination: {
      //   page: action?.payload?.pagination?.page ?? 1,
      //   perPage: action?.payload?.pagination?.perPage ?? 10,
      // },
    });

  // Se siamo alla prima pagina, sostituiamo completamente i dati
  // Altrimenti concateniamo i nuovi dati con quelli esistenti
  const updatedRooms = {
    data:
      Number(action?.payload?.pagination?.page) > 0
        ? [...(currentPaginatedRooms || []), ...newRooms.data]
        : newRooms.data,
    paginationInfo: newRooms.paginationInfo,
  };

  yield put(setRooms(updatedRooms));
  yield put(roomsActions.getRoomsByProjectDone());
}

function* setLoadingRoomsSaga() {
  yield put(setRoomsLoading());
}

function* setEndLoadingRoomsSaga() {
  yield put(setEndRoomsLoading());
}

function* getRoomByIdSaga(action: PayloadAction<string>) {
  yield put(roomsActions.getRoomByIdInProgress());
  try {
    const rooms: RoomEntity[] = yield select(roomsSelectors.selectRooms);
    const roomMocked = rooms.find((room) => room.id === action.payload);
    // TODO: replace this with the room id api call
    const environment: EnvironmentState = yield select(
      environmentSelectors.environment,
    );
    const room: RoomEntity = yield SpaceService.getSpaceByID(
      `${environment.REACT_APP_ASSET_API_REST_URI}${environment.REACT_APP_SPACE_API_BASE_URL}`,
      action.payload,
    );
    const mergedRoom = { ...roomMocked, ...room };
    yield put(setCurrentRoom(mergedRoom));
    yield put(roomsActions.getRoomByIdDone());
  } catch (err) {
    yield put(roomsActions.getRoomByIdError(err));
  }
}

function* setLoadingRoomByIdSaga() {
  yield put(setRoomLoading());
}

function* setEndLoadingRoomByIdSaga() {
  yield put(setEndRoomLoading());
}

function* setErrorGetRoomByIdSaga(action: PayloadAction<unknown>) {
  logger.error(action.payload);
  window.location.replace(routesPaths.spaces);
  yield put(setEndRoomLoading());
}

export function* roomsSaga() {
  yield takeEvery(
    roomsActions.getRoomsByProjectRequested,
    getRoomsByProjectSaga,
  );
  yield takeEvery(
    roomsActions.getRoomsByProjectInProgress,
    setLoadingRoomsSaga,
  );
  yield takeEvery(roomsActions.getRoomsByProjectDone, setEndLoadingRoomsSaga);

  yield takeEvery(roomsActions.getRoomByIdRequested, getRoomByIdSaga);
  yield takeEvery(roomsActions.getRoomByIdInProgress, setLoadingRoomByIdSaga);
  yield takeEvery(roomsActions.getRoomByIdDone, setEndLoadingRoomByIdSaga);
  yield takeEvery(roomsActions.getRoomByIdError, setErrorGetRoomByIdSaga);
}
